import type { IMediaAPI } from '../../api/interface';
import type { IWorkspaceDB } from '../../db/interface';
import type { IMediaCacheController } from '../../util/interface';
import type { IAuthenticationManager } from '../interface/auth';
import type { MediaBinaryStorageTypeMap } from '../interface/media';

function downloadToBinary<
    T extends keyof MediaBinaryStorageTypeMap,
    U extends MediaBinaryStorageTypeMap[T],
>(
    database: IWorkspaceDB,
    auth: IAuthenticationManager,
    cacheController: IMediaCacheController,
    mediaAPIs: Array<IMediaAPI>,
    mediaId: string,
    cacheOnly: boolean,
    type?: T,
): Promise<U> {
    return new Promise(async function (resolve, reject) {
        try {
            await database.open();

            /* do this first, as if it doesn't exist, lets crash out early before network requests */
            const medium = await database.get('IMediumDto', mediaId);
            let data: U | undefined = (await cacheController
                .cachedResponse(mediaId, type ?? 'Blob')
                .catch(error => undefined)) as U | undefined;

            if (!cacheOnly && !data) {
                const token = auth.hasExpired() ? await auth.renew() : auth.accessToken;

                for (const api of mediaAPIs) {
                    const mediaBinary = await api
                        .getMedia(type ?? 'Blob', mediaId, { accessToken: token })
                        .catch(error => undefined);

                    if (mediaBinary === undefined) {
                        /* failed, try next api */
                        continue;
                    } else if (type === 'Blob') {
                        data = mediaBinary as U;
                    } else if (type === 'ArrayBuffer') {
                        data = mediaBinary as U;
                    } else {
                        /* type === 'none' */
                    }
                }
            }

            database.close();

            if (data) {
                if (type === 'Blob') {
                    /* make sure to specify the mime-type in the blob, otherwise media such as mp3/mp4's won't play on Safari */
                    data = new Blob([data as Blob], { type: medium.mimeType }) as U;
                }

                resolve(data);
            } else {
                reject(`Unable to find data for mediaId:${mediaId}`);
            }
        } catch (error) {
            reject(error);
        }
    });
}

function downloadToBinaries<
    T extends keyof MediaBinaryStorageTypeMap,
    U extends MediaBinaryStorageTypeMap[T],
>(
    database: IWorkspaceDB,
    auth: IAuthenticationManager,
    cacheController: IMediaCacheController,
    mediaAPI: Array<IMediaAPI>,
    mediaIds: Array<string>,
    cacheOnly: boolean,
    type?: T,
): Promise<Array<U>> {
    return new Promise(async function (resolve, reject) {
        try {
            const promises = mediaIds.map(id =>
                downloadToBinary<T, U>(
                    database,
                    auth,
                    cacheController,
                    mediaAPI,
                    id,
                    cacheOnly,
                    type,
                ),
            );

            const results = await Promise.all(promises);

            resolve(results);
        } catch (error) {
            reject(error);
        }
    });
}

function downloadToUrl(
    database: IWorkspaceDB,
    auth: IAuthenticationManager,
    cacheController: IMediaCacheController,
    mediaAPI: Array<IMediaAPI>,
    mediaId: string,
    cacheOnly: boolean,
): Promise<string> {
    return new Promise(function (resolve, reject) {
        downloadToBinary(database, auth, cacheController, mediaAPI, mediaId, cacheOnly, 'Blob')
            .then(blob => {
                const url = URL.createObjectURL(blob);
                resolve(url);
            })
            .catch(error => reject(error));
    });
}

function downloadToUrls(
    database: IWorkspaceDB,
    auth: IAuthenticationManager,
    cacheController: IMediaCacheController,
    mediaAPI: Array<IMediaAPI>,
    mediaIds: Array<string>,
    cacheOnly: boolean,
): Promise<Array<string>> {
    return new Promise(function (resolve, reject) {
        downloadToBinaries(database, auth, cacheController, mediaAPI, mediaIds, cacheOnly, 'Blob')
            .then(blobs => {
                const urls = blobs.map(blob => URL.createObjectURL(blob));
                resolve(urls);
            })
            .catch(error => reject(error));
    });
}

const module = {
    downloadToUrl,
    downloadToUrls,
    downloadToBinary,
    downloadToBinaries,
};

export default module;
